import React from 'react';
import { isSrReferralCancelled, Referral } from '../../services/apiService';

export type SrChangeBannerVariant = 'addendum' | 'cancel' | 'post_completion_cancel';

export function getSrChangeBannerVariant(referral: Referral): SrChangeBannerVariant | null {
  if (referral.was_completed_at_cancel && isSrReferralCancelled(referral)) {
    return 'post_completion_cancel';
  }
  if (isSrReferralCancelled(referral)) {
    return 'cancel';
  }
  if (referral.latest_addendum_at) {
    return 'addendum';
  }
  return null;
}

function formatDisplayDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface SrChangeBannerProps {
  referral: Referral;
  onViewAddendum?: (attachmentId: string) => void;
}

export const SrChangeBanner: React.FC<SrChangeBannerProps> = ({ referral, onViewAddendum }) => {
  const variant = getSrChangeBannerVariant(referral);
  if (!variant) return null;

  const addendumWasCompleted =
    variant === 'addendum' &&
    referral.status === 'new' &&
    Boolean(referral.latest_addendum_at);

  if (variant === 'addendum') {
    return (
      <div className="admin-notice admin-notice--warning" style={{ margin: '0 0 12px' }}>
        <strong>
          {addendumWasCompleted
            ? 'This completed referral has received an addendum from SR'
            : 'This referral has received an addendum from SR'}
        </strong>
        <div style={{ marginTop: 4 }}>
          Received {formatDisplayDate(referral.latest_addendum_at)}
        </div>
        {addendumWasCompleted && (
          <div style={{ marginTop: 4 }}>
            Review addendum — referral may need re-entry in Indici.
          </div>
        )}
        {referral.latest_addendum_attachment_id && onViewAddendum && (
          <button
            type="button"
            className="btn-link"
            style={{ marginTop: 8, padding: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
            onClick={() => onViewAddendum(referral.latest_addendum_attachment_id!)}
          >
            View addendum PDF →
          </button>
        )}
      </div>
    );
  }

  if (variant === 'post_completion_cancel') {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="admin-notice admin-notice--error"
        style={{ margin: '0 0 12px', borderLeftWidth: 4 }}
      >
        <strong>This referral was cancelled by the referring GP via SR after it was marked complete.</strong>
        {referral.sr_cancelled_at && (
          <div style={{ marginTop: 4 }}>
            Cancelled {formatDisplayDate(referral.sr_cancelled_at)}
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          This referral may still appear in Indici. Please cancel or update the entry in Indici manually.
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="admin-notice admin-notice--error"
      style={{ margin: '0 0 12px' }}
    >
      <strong>This referral was cancelled by the referring GP via SR</strong>
      {referral.sr_cancelled_at && (
        <div style={{ marginTop: 4 }}>
          Cancelled {formatDisplayDate(referral.sr_cancelled_at)}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        SR actions are unavailable. This referral remains available for history.
      </div>
    </div>
  );
};
